import React, { useState, useEffect } from "react";
import "./attendee.css";
import AttendeeMeetingView from "../../../components/mediaplayer/attendee-meeting-sdk";
import EiqChatPanel from "../../../components/eiq-chat/eiq-chat-panel";
import EiqQnaPanel from "../../../components/eiq-qna/eiq-qna-panel";
import { useCustomChatQnaToggle } from "../../../hooks/useCustomChatQnaToggle";
import PresentationViewer from "../../../components/slider/attendee-slider/ppt-view";
import SpeakerBio from "../../../components/speakerbio/speaker-bio";
import RelatedContent from "../../../components/speakerbio/related-content";
import CpeProgressWidget, {
  useAttendedMinutes,
  CpeProgressPollMarker,
} from "../../../components/cpe-progress/cpe-progress-widget";
import { useParams } from "react-router-dom";
import { EventFetchSlides } from "../../../services/event/attendee-event-slides";
import Loader from "../../../utils/loader/loader"; // Import the Loader component
import { fetchPollAllResults } from "../../../services/event/pollAllResults";

interface SdkData {
  hideQna?: boolean | undefined;
  meetingNumber: string;
  password: string;
  userName: string;
  userEmail: string;
  signature: string;
  tk?: string;
  jobTitle?: string;
}

interface Question {
  pollId: string;
  questionText: string;
  options: Record<string, string>;
  sequence: number;
}

interface PollResult {
  option: string;
  percentage: number;
  voteCount: number;
}

interface PollResultData {
  questionTitle: string;
  totalVotes: number;
  results: PollResult[];
}
interface customSlide{
  collateralTypeId:string;
  title:string;
  description:string;
  hyperlinks: any;
}

const AttendeeDashboardView: React.FC<{ sdkData: SdkData; eventData: any }> = ({
  sdkData,
  eventData,
}) => {
  const { eventId } = useParams();
  const [slides, setSlides] = useState<any[]>([]);
  const [speakerBio, setSpeakerBio] = useState<any[]>([]);
  const [relatedContent, setRelatedContent] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pollQuestions, setPollQuestions] = useState<Question[]>([]);
  const [allPollResults, setAllPollResults] = useState<PollResultData[]>([]);
  const [defaultSpeakers, setDefaultSpeakers] = useState<any[]>([]);
  const [meetingHeight, setMeetingHeight] = useState(0);
  const [meetingSdkViewFlag, setMeetingSdkViewFlag] = useState<any>([]);
  const [isSlides, setIsSlides] = useState<any>([]);
  const [isRelatedContent, setIsRelatedContent] = useState<any>([]);
  const [startMeeting, setStartMeeting] = useState<any>(false);
  const [responsiveMeetingHeight, setResponsiveMeetingHeight] = useState(0);
  const [isZoomSdkExpanded, setIsZoomSdkExpanded] = useState(false);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [qnaHeight, setQnaHeight] = useState(0);
  const [attendeeTitle, setAttendeeTitle] = useState(27);
  const [bioWrapperMarginTop, setBioWrapperMarginTop] = useState(0);
  const [hyperLinks, setHyperLinks] = useState<any[]>([]);
  const [customSlidesArr, setCustomSlidesArr] = useState<customSlide[]>([]);
  const [showGalleryView, setShowGalleryView] = useState(false);
  const [maxVideos, setMaxVideos] = useState(0);
  // sessionStorage-cached at login; fetchData() below refreshes from DB.
  const [latestEnableCustomChatQnA, setLatestEnableCustomChatQnA] = useState<boolean>(
    !!eventData?.enableCustomChatQnA,
  );

  const { enabled: customChatQnaEnabled } = useCustomChatQnaToggle({
    eventid: String(eventId ?? ""),
    initial: latestEnableCustomChatQnA,
  });

  useEffect(() => {
  }, [isLoading]);

  useEffect(() => {
    const words = eventData?.title.split(" ");
    if (words.length > 10) {
      const excessWords = words.length - 10;
      setAttendeeTitle(Math.max(22, 19 - excessWords)); // Decrease font size, but keep a minimum size
    } else {
      setAttendeeTitle(16); // Default font size
    }
  }, [eventData.title]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await EventFetchSlides(Number(eventId));
        setSlides(response);
        setSpeakerBio(response?.collaterals?.speakerbio);
        setRelatedContent(response?.collaterals?.attachments);
        setPollQuestions(response.collaterals.pollQuestions);
        setDefaultSpeakers(response.defaultSpeakers);
        setShowChatPanel(response.showChatPanel);
        setHyperLinks(response?.hyperlinks);
        setCustomSlidesArr(response.collaterals.customSlidesArr);
        setShowGalleryView(response.showGalleryView);
        setMaxVideos(response.maximumVideosInGalleryView);
        // Live value from DB — overrides the stale cached eventData.
        setLatestEnableCustomChatQnA(!!response.enableCustomChatQnA);
        // Keep the sessionStorage cache in sync so any other consumer
        // that reads eventData from session picks up the fresh value.
        try {
          const cached = sessionStorage.getItem("eventData");
          if (cached) {
            const parsed = JSON.parse(cached);
            parsed.enableCustomChatQnA = !!response.enableCustomChatQnA;
            sessionStorage.setItem("eventData", JSON.stringify(parsed));
          }
        } catch {
          /* ignore cache write errors */
        }
        // response.collaterals.slides = [];
        // If slides data is empty, set custom; otherwise set default
        if (response?.collaterals?.slides.length === 0) {
          setMeetingSdkViewFlag('custom');
          setIsSlides(false);
          setStartMeeting(true);
        } else {
          setMeetingSdkViewFlag('default');
          setIsSlides(true);
          setStartMeeting(true);
          setIsZoomSdkExpanded(response.showSdkLargeView);
        }

        if (response?.collaterals?.attachments.length > 0 || response?.hyperlinks.length > 0) {
          setIsRelatedContent(true);
        }else{
          setIsRelatedContent(false);
        }
      } catch (error) {
        console.error("Error fetching slides:", error);
      }
    };

    fetchData();
  }, [eventId]);

  useEffect(() => {
    const fetchPollResult = async () => {
      try {
        const response = await fetchPollAllResults(Number(eventId));
        setAllPollResults(response);
      } catch (error) {
        console.error("Error fetching poll results:", error);
      }
    };

    fetchPollResult();
  }, [eventId]);

  const getHeightOfMeeting = (height: number) => {
    setMeetingHeight(height);
  };

  const getHeightOfMeetingResponsive = (height: number) => {
    if(meetingSdkViewFlag == 'custom'){
      setResponsiveMeetingHeight(height - 20);
    }else{
      setResponsiveMeetingHeight(height);
    }
  };

  const getHeightOfQna = (height: number) => {
    setQnaHeight(height);
  };
  
  const isMobileDevice = () => window.innerWidth <= 768; // Define the breakpoint for mobile devices

  const getHeightLiveViewResponsive = () => {
    if(window.innerWidth <= 768){
    const presentationViewer = document.getElementById("presentationViewer");
    const presentationHeight = presentationViewer?.offsetHeight || 0;
    const meetingSDKElement = document.getElementById("meetingSDKElement") as HTMLElement;
    const childElement = meetingSDKElement.querySelector('[aria-label="Zoom Web SDK Widget"]') as HTMLElement;
     if (childElement) {
        const height = childElement.clientHeight;
        setResponsiveMeetingHeight(height);
        const gaptemp = isZoomSdkExpanded ? 20 : 40;
        const marginTop = presentationHeight + height + gaptemp; // Adjust as needed
        setBioWrapperMarginTop(marginTop);
      }
    }
  };

  const calculateMarginTop = () => {
    const presentationViewer = document.getElementById("presentationViewer");
    const presentationHeight = presentationViewer?.offsetHeight || 0;
    const gaptemp = isZoomSdkExpanded ? 20 : 40;
    const marginTop = presentationHeight + responsiveMeetingHeight + gaptemp; // Adjust as needed
    if(window.innerWidth <= 768){
      setBioWrapperMarginTop(marginTop);
    }
  };

  useEffect(() => {
    // Initial calculation
    calculateMarginTop();
    // Recalculate on window resize
    window.addEventListener("resize", calculateMarginTop);
    return () => {
      window.removeEventListener("resize", calculateMarginTop);
    };
  }, [responsiveMeetingHeight]);

  // Recompute marginTop on slide/image reflow (mobile bio-overlap fix).
  useEffect(() => {
    if (!isMobileDevice()) return;
    const target = document.getElementById("presentationViewer");
    if (!target || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => calculateMarginTop());
    observer.observe(target);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSlides, isZoomSdkExpanded, customChatQnaEnabled]);

  let speakerMarginTop;
  let relatedContentMarginTop;

  if (isMobileDevice()) {
    if (customChatQnaEnabled || isZoomSdkExpanded) {
      // Mobile inline-flow layout — elements flow naturally, no offset needed.
      speakerMarginTop = 0;
      relatedContentMarginTop = 0;
    } else {
      speakerMarginTop = bioWrapperMarginTop;
      const bioSpeaker = document.getElementById("bio-speaker");
      const bioSpeakernHeight = bioSpeaker?.offsetHeight || 0;
      relatedContentMarginTop = bioSpeakernHeight + 40;
    }
  } else {
    // Tablet (≤992) in expanded/custom flow uses CSS flex-order, so the
    // desktop 315px / meetingHeight+10 inline value would punch a gap.
    const inTabletResponsiveFlow =
      (isZoomSdkExpanded || customChatQnaEnabled) &&
      typeof window !== "undefined" &&
      window.innerWidth <= 992;
    if (inTabletResponsiveFlow) {
      speakerMarginTop = 0;
    } else {
      speakerMarginTop = !isZoomSdkExpanded ? meetingHeight + 10 : '315px';
    }
    // Custom mode: Eiq Q&A is in flex flow; no offset needed.
    // Zoom mode: popper is absolute, so push related-content below it.
    relatedContentMarginTop = customChatQnaEnabled ? 0 : qnaHeight + 15;
  }

  // CPE progress widget — additive, gated on event config.
  const cpeProgressVisible: boolean =
    !!eventData?.certificateEnabled &&
    !!eventData?.certificateType &&
    eventData.certificateType !== "none";
  const cpeProgressType: "cpe" | "shrm" =
    eventData?.certificateType === "shrm" ? "shrm" : "cpe";
  const cpeRequiredMinutes: number = Number(eventData?.cpeMinMinutes ?? 50);
  const cpeIsLive: boolean = !!eventData?.isMeetingLive;
  const cpeStartedAt: string | Date | null =
    eventData?.liveStartedAt ?? eventData?.currentWebinarStartTime ?? null;
  const cpeAttendedMinutes: number = useAttendedMinutes(
    cpeStartedAt,
    cpeIsLive,
  );
  const cpePollMarkers: CpeProgressPollMarker[] = (pollQuestions ?? []).map(
    (p, idx) => ({
      pollId: p.pollId ?? String(idx),
      sequence: p.sequence ?? idx + 1,
      // TODO: wire to per-attendee answered state when /progress endpoint lands.
      answered: false,
    }),
  );

  return (
    <div className="attendee-main-panel">
      {isLoading && <Loader />}
      <div className="attendee-header">
        <div className="event-title-attendee" style={{
              fontSize: `${attendeeTitle}px`,
              transition: "font-size 0.3s ease",
            }}>{eventData?.title}</div>
      </div>
      <div
        className={`main-inner-wrapper ${
          customChatQnaEnabled ? "main-inner-wrapper--custom-stack" : ""
        }`}
      >
        <div className="inner-left-side">
          <div className="attendee-meeting-wrapper" id="attendeeMeetingView">
            {isZoomSdkExpanded ? (
              <div
                className={`${isZoomSdkExpanded ? "live-short-view" : ""}`}
                style={{
                  marginTop: responsiveMeetingHeight
                    ? responsiveMeetingHeight + 20
                    : undefined,
                }}
              >
                <div id="presentationViewer">
                  <PresentationViewer
                    slidesData={slides}
                    pollData={pollQuestions}
                    allPollResultData={allPollResults}
                    email={sdkData.userEmail}
                    getHeightLiveViewResponsive={getHeightLiveViewResponsive}
                    customSlidesData={customSlidesArr}
                  />
                </div>
              </div>
            ) : (
              startMeeting && (
                <AttendeeMeetingView
                  meetingNumber={sdkData.meetingNumber}
                  password={sdkData.password}
                  userName={sdkData.userName}
                  userEmail={sdkData.userEmail}
                  leaveUrl="https://example.com"
                  signature={sdkData.signature}
                  tk={sdkData.tk}
                  setLoading={setIsLoading}
                  meetingSdkViewFlag={meetingSdkViewFlag}
                  getHeightOfMeeting={getHeightOfMeeting}
                  hideQna={sdkData?.hideQna}
                  getHeightOfMeetingResponsive={getHeightOfMeetingResponsive}
                  showChat={showChatPanel}
                  getHeightOfQna={getHeightOfQna}
                  jobTitle={sdkData.jobTitle}
                  showGalleryView={showGalleryView}
                  maxVideos={maxVideos}
                  hideZoomChatQna={customChatQnaEnabled}
                />
              )
            )}
            <div
              className="attendee-presentationview-wrapper"
              style={{
                marginTop: responsiveMeetingHeight
                  ? responsiveMeetingHeight + 20
                  : undefined,
              }}
            >
              {isZoomSdkExpanded ? (
                <AttendeeMeetingView
                  meetingNumber={sdkData.meetingNumber}
                  password={sdkData.password}
                  userName={sdkData.userName}
                  userEmail={sdkData.userEmail}
                  leaveUrl="https://example.com"
                  signature={sdkData.signature}
                  tk={sdkData.tk}
                  setLoading={setIsLoading}
                  meetingSdkViewFlag={meetingSdkViewFlag}
                  getHeightOfMeeting={getHeightOfMeeting}
                  hideQna={sdkData?.hideQna}
                  getHeightOfMeetingResponsive={getHeightOfMeetingResponsive}
                  isZoomSdkExpanded={isZoomSdkExpanded}
                  showChat={showChatPanel}
                  getHeightOfQna={getHeightOfQna}
                  jobTitle={sdkData.jobTitle}
                  showGalleryView={showGalleryView}
                  maxVideos={maxVideos}
                  hideZoomChatQna={customChatQnaEnabled}
                />
              ) : (
                isSlides && (
                  <div id="presentationViewer">
                    <PresentationViewer
                      slidesData={slides}
                      pollData={pollQuestions}
                      allPollResultData={allPollResults}
                      email={sdkData.userEmail}
                      getHeightLiveViewResponsive={getHeightLiveViewResponsive}
                      customSlidesData={customSlidesArr}
                    />
                  </div>
                )
              )}
            </div>
          </div>
          {!isLoading && !showChatPanel && (
            <div
              className={`right-attendee-speaker-bio-wrapper${
                cpeProgressVisible ? " has-cpe-progress" : ""
              }`}
              id="bio-speaker"
              style={{ marginTop: speakerMarginTop }}
            >
              <SpeakerBio
                speakerBio={speakerBio}
                defaultSpeakers={defaultSpeakers}
                isZoomSdkExpanded={isZoomSdkExpanded}
              />
              {cpeProgressVisible && (
                <CpeProgressWidget
                  certificateType={cpeProgressType}
                  attendedMinutes={cpeAttendedMinutes}
                  requiredMinutes={cpeRequiredMinutes}
                  pollMarkers={cpePollMarkers}
                  isLive={cpeIsLive}
                  eventId={eventId}
                  email={sdkData.userEmail}
                />
              )}
            </div>
          )}
        </div>
        {!isLoading && (
          <div className="inner-right-side">
            {/* Always mounted; subscription survives toggle flips. */}
            <div
              className="eiq-attendee-qna-wrapper eiq-attendee-qna-wrapper--tall"
              style={{ display: customChatQnaEnabled ? undefined : "none" }}
              aria-hidden={!customChatQnaEnabled}
            >
              <EiqQnaPanel
                webinarId={String(sdkData.meetingNumber)}
                userId={sdkData.userEmail}
                userName={sdkData.userName}
                userEmail={sdkData.userEmail}
                role="ATTENDEE"
              />
            </div>
            {showChatPanel && (
              <div
                className="eiq-attendee-chat-below-sdk"
                style={{ display: customChatQnaEnabled ? undefined : "none" }}
                aria-hidden={!customChatQnaEnabled}
              >
                <EiqChatPanel
                  webinarId={String(sdkData.meetingNumber)}
                  userId={sdkData.userEmail}
                  userName={sdkData.userName}
                  userEmail={sdkData.userEmail}
                  role="ATTENDEE"
                />
              </div>
            )}
            {showChatPanel && (
              <div className="attendee-speaker-bio-wrapper" id="bio-speaker">
                <SpeakerBio speakerBio={speakerBio} defaultSpeakers={defaultSpeakers} />
              </div>
            )}
            {sdkData?.hideQna && isRelatedContent && (
              <div
                className={`related-content-wrapper custom-related-content-qa ${
                  meetingSdkViewFlag === "custom" && meetingHeight ? "custom-related-content-qa" : ""
                }`}
                id="relatedContent"
                style={{
                  marginTop:
                    meetingSdkViewFlag === "custom" && meetingHeight ? meetingHeight + 10 : undefined,
                  left:
                    meetingSdkViewFlag === "custom" && meetingHeight ? "450px" : undefined,
                  width:
                    meetingSdkViewFlag === "custom" && meetingHeight
                      ? "calc(100vw - 760px)"
                      : undefined,
                }}
              >
                <RelatedContent
                  relatedContent={relatedContent}
                  hyperLinks={hyperLinks}
                  eventId={eventId}
                  email={sdkData.userEmail}
                />
              </div>
            )}
            {!sdkData?.hideQna && isRelatedContent && (
              <div
                className={`related-content-wrapper ${
                  meetingSdkViewFlag === "custom" && meetingHeight ? "custom-related-content" : ""
                }`}
                id="relatedContent"
                style={{
                  marginTop: relatedContentMarginTop,
                  width: "285px",
                }}
              >
                <RelatedContent
                  relatedContent={relatedContent}
                  hyperLinks={hyperLinks}
                  eventId={eventId}
                  email={sdkData.userEmail}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div >
  );
};

export default AttendeeDashboardView;
